import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getWorkflows, getWorkflow, getFlaggedItems } from '../services/workflows';
import { getCurrentProfile } from '../services/profile';
import { getStepName } from '../utils/stepName';
import {
  fetchDepartments,
  createDepartment,
  updateDepartment as updateDepartmentService,
  deleteDepartment as deleteDepartmentService,
  fetchWorkflowAssignments,
  assignWorkflowDepartment,
} from '../services/departments';
import {
  getUsers,
  setUserDepartmentRole,
  removeUserDepartmentRole,
} from '../services/users';
import { subscribe, unsubscribe } from '../services/realtime';

const DEFAULT_PROFILE = {
  id: '', firstName: '', lastName: '', email: '', role: '', description: '', avatarUrl: null,
  departmentRoles: {},
};

const ROLE_PRIORITY = { Admin: 3, Manager: 2, Viewer: 1 };

const EMPTY_ROI_SETTINGS = Object.freeze({});

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [workflows, setWorkflowsRaw] = useState([]);
  const [workflowDeptMap, setWorkflowDeptMap] = useState({}); // { [workflowId]: deptId }
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeDepartment, setActiveDepartmentState] = useState(null);
  const [flaggedItems, setFlaggedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfileState] = useState(DEFAULT_PROFILE);
  const [profileOpen, setProfileOpenState] = useState(false);
  const [activePanel, setActivePanelState] = useState(null);

  // Enrich workflows with departmentId by joining with AMC's mapping.
  const workflowsWithDept = useMemo(() => {
    if (!workflows.length) return workflows;
    if (!Object.keys(workflowDeptMap).length) return workflows;
    return workflows.map((w) => {
      const deptId = workflowDeptMap[w.id] ?? (w.workflowId ? workflowDeptMap[w.workflowId] : undefined);
      return deptId ? { ...w, departmentId: deptId } : w;
    });
  }, [workflows, workflowDeptMap]);

  // Load initial data from real services
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wfs, flags, profile, depts, assignments, usrs] = await Promise.allSettled([
          getWorkflows(),
          getFlaggedItems(),
          getCurrentProfile(),
          fetchDepartments(),
          fetchWorkflowAssignments(),
          getUsers().catch(() => []),
        ]);
        if (cancelled) return;
        if (wfs.status === 'fulfilled') setWorkflowsRaw(wfs.value);
        if (flags.status === 'fulfilled') setFlaggedItems(flags.value);
        if (profile.status === 'fulfilled') setUserProfileState(profile.value);
        if (depts.status === 'fulfilled') setDepartments(depts.value);
        if (assignments.status === 'fulfilled') setWorkflowDeptMap(assignments.value);
        if (usrs.status === 'fulfilled') setUsers(usrs.value);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Real-time updates via WebSocket (debounced).
  const refetchTimer = useRef(null);
  useEffect(() => {
    const scheduleRefetch = (payload) => {
      const workflowId = payload?.workflowId;
      if (workflowId) {
        clearTimeout(refetchTimer.current);
        refetchTimer.current = setTimeout(async () => {
          const updated = await getWorkflow(workflowId);
          if (updated) {
            setWorkflowsRaw((prev) => {
              const idx = prev.findIndex((w) => w.id === updated.id);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = updated;
                return next;
              }
              return [...prev, updated];
            });
          }
        }, 300);
        return;
      }
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(async () => {
        const wfs = await getWorkflows();
        setWorkflowsRaw(wfs);
      }, 500);
    };

    subscribe('workflow:updated', scheduleRefetch);
    subscribe('workflow:completed', scheduleRefetch);
    subscribe('workflow:failed', scheduleRefetch);

    return () => {
      clearTimeout(refetchTimer.current);
      unsubscribe('workflow:updated', scheduleRefetch);
      unsubscribe('workflow:completed', scheduleRefetch);
      unsubscribe('workflow:failed', scheduleRefetch);
    };
  }, []);

  // ---------- Department state + derivations ----------

  const currentUser = userProfile.id ? userProfile : null;

  const departmentsEnabled = departments.length > 0;

  const accessibleDepartments = useMemo(() => {
    if (!departmentsEnabled || !currentUser) return [];
    if (currentUser.role === 'Admin') return departments;
    const myDeptIds = new Set(Object.keys(currentUser.departmentRoles ?? {}));
    return departments.filter((d) => myDeptIds.has(d.id));
  }, [departments, departmentsEnabled, currentUser]);

  const accessibleWorkflows = useMemo(() => {
    if (!departmentsEnabled) return workflowsWithDept;
    if (currentUser?.role === 'Admin' && !activeDepartment) return workflowsWithDept;
    if (activeDepartment) {
      return workflowsWithDept.filter((w) => w.departmentId === activeDepartment.id);
    }
    const accessibleIds = new Set(accessibleDepartments.map((d) => d.id));
    return workflowsWithDept.filter((w) => w.departmentId && accessibleIds.has(w.departmentId));
  }, [workflowsWithDept, departmentsEnabled, currentUser, activeDepartment, accessibleDepartments]);

  const effectiveRole = useMemo(() => {
    if (!currentUser) return 'Viewer';
    if (currentUser.role === 'Admin') return 'Admin';
    if (activeDepartment) {
      return currentUser.departmentRoles?.[activeDepartment.id] ?? 'Viewer';
    }
    const deptRoles = Object.values(currentUser.departmentRoles ?? {});
    if (!deptRoles.length) return 'Viewer';
    return deptRoles.reduce(
      (best, r) => ((ROLE_PRIORITY[r] ?? 0) > (ROLE_PRIORITY[best] ?? 0) ? r : best),
      'Viewer',
    );
  }, [currentUser, activeDepartment]);

  // Reset activeDepartment if it becomes inaccessible (e.g. role revoked)
  useEffect(() => {
    if (activeDepartment && !accessibleDepartments.some((d) => d.id === activeDepartment.id)) {
      setActiveDepartmentState(null);
    }
  }, [accessibleDepartments, activeDepartment]);

  // ---------- Department action callbacks ----------

  const setActiveDepartment = useCallback((deptOrNull) => {
    setActiveDepartmentState(deptOrNull);
  }, []);

  const addDepartment = useCallback(async (name) => {
    const dept = await createDepartment(name);
    setDepartments((prev) => [...prev, dept]);
    return dept;
  }, []);

  const updateDepartmentCtx = useCallback(async (id, patch) => {
    await updateDepartmentService(id, patch);
    setDepartments((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  }, []);

  const deleteDepartmentCtx = useCallback(async (id) => {
    await deleteDepartmentService(id);
    setDepartments((prev) => prev.filter((d) => d.id !== id));
    setWorkflowDeptMap((prev) => {
      const next = { ...prev };
      for (const [wfId, dId] of Object.entries(prev)) if (dId === id) delete next[wfId];
      return next;
    });
    setActiveDepartmentState((cur) => (cur?.id === id ? null : cur));
  }, []);

  const assignWorkflowToDept = useCallback(async (workflowId, deptId) => {
    await assignWorkflowDepartment(workflowId, deptId);
    setWorkflowDeptMap((prev) => {
      const next = { ...prev };
      if (deptId) next[workflowId] = deptId;
      else delete next[workflowId];
      return next;
    });
  }, []);

  const addUserToDept = useCallback(async (oid, deptId, role) => {
    await setUserDepartmentRole(oid, deptId, role);
    setUsers((prev) => prev.map((u) => (u.id === oid
      ? { ...u, departmentRoles: { ...u.departmentRoles, [deptId]: role } }
      : u)));
  }, []);

  const removeUserFromDept = useCallback(async (oid, deptId) => {
    await removeUserDepartmentRole(oid, deptId);
    setUsers((prev) => prev.map((u) => {
      if (u.id !== oid) return u;
      const next = { ...u.departmentRoles };
      delete next[deptId];
      return { ...u, departmentRoles: next };
    }));
  }, []);

  // ---------- Flagging ----------

  const handleFlag = useCallback((workflowId, stepId) => {
    const wf = workflows.find((w) => w.id === workflowId);
    const step = wf?.steps.find((s) => s.id === stepId);
    if (!wf || !step) return;

    setFlaggedItems((prev) => {
      if (prev.some((f) => f.workflowId === workflowId && f.stepId === stepId)) return prev;
      return [...prev, {
        workflowId,
        stepId,
        workflowName: wf.name,
        stepName: getStepName(step),
        agentName: step.agentName,
        reasoning: step.reasoning,
        flaggedAt: new Date().toISOString(),
        status: step.status,
      }];
    });
  }, [workflows]);

  const handleUnflag = useCallback((workflowId, stepId) => {
    setFlaggedItems((prev) => prev.filter((f) => !(f.workflowId === workflowId && f.stepId === stepId)));
  }, []);

  const isFlagged = useCallback((workflowId, stepId) => {
    return flaggedItems.some((f) => f.workflowId === workflowId && f.stepId === stepId);
  }, [flaggedItems]);

  // ---------- Workflow operations ----------

  const updateWorkflow = useCallback((id, changes) => {
    setWorkflowsRaw((prev) => prev.map((wf) => (wf.id === id ? { ...wf, ...changes } : wf)));
  }, []);

  const hydrateWorkflow = useCallback(async (id) => {
    if (!id) return null;
    const updated = await getWorkflow(id);
    if (!updated) return null;
    setWorkflowsRaw((prev) => {
      const idx = prev.findIndex((w) => w.id === updated.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updated;
        return next;
      }
      return [...prev, updated];
    });
    return updated;
  }, []);

  // ---------- Profile / UI chrome ----------

  const setUserProfile = useCallback((updates) => {
    setUserProfileState((prev) => ({ ...prev, ...updates }));
  }, []);

  const setProfileOpen = useCallback((open) => {
    setProfileOpenState(open);
    setActivePanelState(open ? 'profile' : null);
  }, []);

  const setActivePanel = useCallback((panel) => {
    setActivePanelState(panel);
  }, []);

  // ROI settings kept as a no-op shape for backward compatibility — per-workflow
  // rates now live on AFD's workflows table.
  const setRoiSettings = useCallback(() => {}, []);

  const contextValue = useMemo(() => ({
    // workflows
    workflows: workflowsWithDept,
    accessibleWorkflows,
    flaggedItems,
    handleFlag,
    handleUnflag,
    isFlagged,
    updateWorkflow,
    hydrateWorkflow,
    // departments
    departments,
    departmentsEnabled,
    accessibleDepartments,
    activeDepartment,
    setActiveDepartment,
    addDepartment,
    updateDepartment: updateDepartmentCtx,
    deleteDepartment: deleteDepartmentCtx,
    assignWorkflowToDept,
    // users
    users,
    currentUser,
    effectiveRole,
    addUserToDept,
    removeUserFromDept,
    // profile
    userProfile,
    setUserProfile,
    profileOpen,
    setProfileOpen,
    activePanel,
    setActivePanel,
    // misc
    loading,
    setLoading,
    roiSettings: EMPTY_ROI_SETTINGS,
    setRoiSettings,
  }), [
    workflowsWithDept, accessibleWorkflows, flaggedItems, handleFlag, handleUnflag,
    isFlagged, updateWorkflow, hydrateWorkflow,
    departments, departmentsEnabled, accessibleDepartments, activeDepartment,
    setActiveDepartment, addDepartment, updateDepartmentCtx, deleteDepartmentCtx,
    assignWorkflowToDept,
    users, currentUser, effectiveRole, addUserToDept, removeUserFromDept,
    userProfile, setUserProfile, profileOpen, setProfileOpen, activePanel, setActivePanel,
    loading, setRoiSettings,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
